import React, { Fragment, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { ICONS } from 'Components/utils/icon/icons';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import { Hr } from 'Components/frameworks/hr/Hr';
import { Image } from 'Components/frameworks/image/Image';
import { ResponsiveTabs } from 'Components/frameworks/responsive-tabs/ResponsiveTabs';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
import type { GQLProps, CMSProps } from './resolver';
type LineProps = CMSProps & GQLProps;

const Area = (props: LineProps) => {
  const {
    resort: { name: title = '', destination, productVideo, strengths = [] },
  } = props;
  const { description = '' } = destination ?? {};
  return (
    <Fragment>
      <Hr className="my-20" />
      <div className="sm:flex">
        <Title className="sm:w-1/4" theme="title-brand-n1">
          {title}
        </Title>
        <div className="sm:w-3/4">
          <Text text={description} />
          {description ? <Hr className="my-20" /> : null}
          <div className="flex flex-col gap-20 sm:flex-row">
            <div className="sm:basis-1/3">
              <Image imageUrl={productVideo} ratioKey={ratioUtils.XL} />
            </div>
            {!isEmpty(strengths) ? (
              <ul data-testid="Destination-strengths" className="text-14 sm:basis-2/3">
                {strengths.map((strength): React.ReactNode => {
                  return (
                    <li key={strength} className="list-disc">
                      {strength}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const TabContent = ({ content }) => {
  if (!content) {
    return null;
  }

  return <Text text={content} />;
};

function hasResponsiveTabs(practicalInformation) {
  return !!(practicalInformation.healthFormalities || practicalInformation.passportAndVisas);
}

const ProductDestinationsLine = (props: LineProps) => {
  const { healthFormalities, passportAndVisas, resort } = props;
  const { strengths = [], description, practicalInformation } = resort;
  const shouldRenderArea = strengths.length > 0 || !!resort.productVideo;
  const tabsItems = useMemo(() => {
    return [
      {
        header: {
          iconName: ICONS.HEALTH,
          title: healthFormalities,
        },
        content: [<TabContent key="health" content={practicalInformation.healthFormalities} />],
      },
      {
        header: {
          iconName: ICONS.PASSPORT,
          title: passportAndVisas,
        },
        content: [<TabContent key="passport" content={practicalInformation.passportAndVisas} />],
      },
    ];
  }, [
    passportAndVisas,
    healthFormalities,
    practicalInformation.passportAndVisas,
    practicalInformation.healthFormalities,
  ]);
  return (
    <SiteContainer className="Line" data-testid="ProductInformationDestination">
      <div className="flex flex-col gap-20 sm:flex-row">
        <Title className="sm:basis-1/4" theme="title-brand-n1">
          {resort.destination.country}
        </Title>
        <div className="sm:basis-3/4">
          <Text text={description} />
          {hasResponsiveTabs(practicalInformation) && (
            <ResponsiveTabs
              areAllCollapsable
              {...({
                className: 'mt-20',
                isContainerWide: true,
                items: tabsItems,
              } as unknown as React.ComponentProps<typeof ResponsiveTabs>)}
            />
          )}
        </div>
      </div>
      {shouldRenderArea ? <Area {...props} /> : null}
    </SiteContainer>
  );
};

export default ProductDestinationsLine;
